import request from '../../config/axiosConfig';
import * as api from '../../constants/apiEndPoint';

// Generic CRUD service factory for frontend
const createService = (endpoints) => ({
  getList: async (dataBody) => {
    const { data } = await request({ url: endpoints.getList, method: 'POST', data: dataBody });
    return data;
  },
  create: async (dataBody) => {
    const { data } = await request({ url: endpoints.create, method: 'POST', data: dataBody });
    return data;
  },
  update: async (id, dataBody) => {
    const { data } = await request({ url: endpoints.update, method: 'POST', data: { id, ...dataBody } });
    return data;
  },
  delete: async (id) => {
    const { data } = await request({ url: endpoints.delete, method: 'POST', data: { id } });
    return data;
  },
  ...(endpoints.sort ? {
    sort: async (items) => {
      const { data } = await request({ url: endpoints.sort, method: 'POST', data: { items } });
      return data;
    },
  } : {}),
});

// ====== Service instances ======
export const appVersionService = createService({
  getList: api.GET_LIST_APP_VERSIONS, 
  create: api.CREATE_APP_VERSION,
  update: api.UPDATE_APP_VERSION, 
  delete: api.DELETE_APP_VERSION,
  sort: api.SORT_APP_VERSIONS,
});

export const apiVersionService = createService({
  getList: api.GET_LIST_API_VERSIONS,
  create: api.CREATE_API_VERSION,
  update: api.UPDATE_API_VERSION,
});

export const appRateService = createService({
  getList: api.GET_LIST_APP_RATES,
});

export const feedbackService = createService({
  getList: api.GET_LIST_FEEDBACKS,
});

export const configService = {
  ...createService({
    getList: api.GET_LIST_CONFIGS,
    create: api.CREATE_CONFIG,
    update: api.UPDATE_CONFIG,
    delete: api.DELETE_CONFIG,
    sort: api.SORT_CONFIGS,
  }),
  getAudiences: () => request({ url: api.GET_CONFIG_AUDIENCES, method: 'POST' }),
  ...(api.CHANGE_CONFIG_STATUS ? {
    changeStatus: async (id, status) => {
      const { data } = await request({ url: api.CHANGE_CONFIG_STATUS, method: 'POST', data: { id, status } });
      return data;
    }
  } : {}),
  ...(api.COPY_CONFIG ? {
    copy: async (id) => {
      const { data } = await request({ url: api.COPY_CONFIG, method: 'POST', data: { id } });
      return data;
    }
  } : {})
};

// ====== Remote Config: Tập khách hàng (biểu thức boolean) ======
export const audienceGroupService = {
  ...createService({
    getList: api.GET_LIST_AUDIENCE_GROUPS,
    create: api.CREATE_AUDIENCE_GROUP,
    update: api.UPDATE_AUDIENCE_GROUP,
    delete: api.DELETE_AUDIENCE_GROUP,
  }),
  getOne: async (id) => {
    const { data } = await request({ url: api.GET_AUDIENCE_GROUP, method: 'POST', data: { id } });
    return data;
  },
  changeStatus: async (id, status) => {
    const { data } = await request({
      url: api.CHANGE_AUDIENCE_GROUP_STATUS,
      method: 'POST',
      data: { id, status },
    });
    return data;
  },
  getUsage: async (id) => {
    const { data } = await request({ url: api.GET_AUDIENCE_GROUP_USAGE, method: 'POST', data: { id } });
    return data;
  },
  // Gọi debounce từ trình dựng biểu thức. API cố tình trả 200 kèm
  // {valid:false, errors} khi biểu thức sai, chỉ 503 khi không kiểm tra được.
  validateExpression: async (expression, excludeCode) => {
    const { data } = await request({
      url: api.VALIDATE_AUDIENCE_EXPRESSION,
      method: 'POST',
      data: { expression, excludeCode },
    });
    return data;
  },
};

// ====== Remote Config: Lịch sử & Time-travel ======
// Không dùng createService vì đây không phải CRUD.
export const configHistoryService = {
  getHistory: async (params) => {
    const { data } = await request({ url: api.GET_CONFIG_HISTORY, method: 'POST', data: params });
    return data;
  },
  getEvent: async (id) => {
    const { data } = await request({ url: api.GET_CONFIG_EVENT, method: 'POST', data: { id } });
    return data;
  },
  getTimeline: async (params) => {
    const { data } = await request({ url: api.GET_CONFIG_TIMELINE, method: 'POST', data: params });
    return data;
  },
  getMeta: async () => {
    const { data } = await request({ url: api.GET_CONFIG_HISTORY_META, method: 'POST' });
    return data;
  },
  // `at` bỏ trống = xem trước tại thời điểm hiện tại; có `at` = time-travel.
  resolve: async (params) => {
    const { data } = await request({ url: api.RESOLVE_CONFIGS, method: 'POST', data: params });
    return data;
  },
  diff: async (params) => {
    const { data } = await request({ url: api.DIFF_CONFIGS, method: 'POST', data: params });
    return data;
  },
};

export const utilityBannerService = {
  ...createService({
    getList: api.GET_LIST_UTILITY_BANNERS,
    create: api.CREATE_UTILITY_BANNER,
    update: api.UPDATE_UTILITY_BANNER,
    delete: api.DELETE_UTILITY_BANNER,
    sort: api.SORT_UTILITY_BANNERS,
  }),
  changeStatus: async (id, status) => {
    const { data } = await request({ url: api.CHANGE_UTILITY_BANNER_STATUS, method: 'POST', data: { id, status } });
    return data;
  },
  // Xoá mềm nhiều dòng (status = -1). CrudPage thấy hàm này thì bật ô tích chọn + "Xóa nhiều".
  batchDelete: async (ids) => {
    const { data } = await request({ url: api.DELETE_UTILITY_BANNERS, method: 'POST', data: { ids } });
    return data;
  },
  getMetadata: async () => {
    const { data } = await request({ url: api.GET_UTILITY_BANNER_METADATA, method: 'POST', data: {} });
    return data;
  },
  updateTitle: async (position, value) => {
    const { data } = await request({ url: api.UPDATE_UTILITY_BANNER_TITLE, method: 'POST', data: { position, value } });
    return data;
  },
  copy: async (id) => {
    const { data } = await request({ url: api.COPY_UTILITY_BANNER, method: 'POST', data: { id } });
    return data;
  },
};

export const suggestionService = {
  ...createService({
    getList: api.GET_LIST_SUGGESTIONS,
    create: api.CREATE_SUGGESTION,
    update: api.UPDATE_SUGGESTION,
    delete: api.DELETE_SUGGESTION,
  }),
  changeStatus: async (id, status) => {
    const { data } = await request({ url: api.CHANGE_SUGGESTION_STATUS, method: 'POST', data: { id, status } });
    return data;
  },
  getMetadata: async () => {
    const { data } = await request({ url: api.GET_SUGGESTION_METADATA, method: 'POST', data: {} });
    return data;
  },
};

export const profileService = {
  ...createService({
    getList: api.GET_LIST_PROFILES,
    create: api.CREATE_PROFILE,
    update: api.UPDATE_FAMILY_PROFILE,
    delete: api.DELETE_PROFILE,
  }),
  // Xoá cứng nhiều dòng (như "delete-checkbox" của CMS cũ). CrudPage thấy hàm này thì bật ô tích chọn.
  batchDelete: async (ids) => {
    const { data } = await request({ url: api.DELETE_PROFILES, method: 'POST', data: { ids } });
    return data;
  },
};

export const nextCampaignService = createService({
  getList: api.GET_LIST_NEXT_CAMPAIGNS,
  create: api.CREATE_NEXT_CAMPAIGN,
  update: api.UPDATE_NEXT_CAMPAIGN,
  delete: api.DELETE_NEXT_CAMPAIGN,
  ...(api.CHANGE_NEXT_CAMPAIGN_STATUS ? {
    changeStatus: async (id, status) => {
      const { data } = await request({ url: api.CHANGE_NEXT_CAMPAIGN_STATUS, method: 'POST', data: { id, status } });
      return data;
    }
  } : {}),
});

export const voucherService = {
  ...createService({
    getList: api.GET_LIST_VOUCHERS,
    create: api.CREATE_VOUCHER,
    update: api.UPDATE_VOUCHER,
    delete: api.DELETE_VOUCHER,
  }),
  changeStatus: async (id, status) => {
    const { data } = await request({ url: api.CHANGE_VOUCHER_STATUS, method: 'POST', data: { id, status } });
    return data;
  },
  // Nguồn dữ liệu cho các select của form/bộ lọc: CTKM, dịch vụ, gói premium, topics, audiences
  getMetadata: async () => {
    const { data } = await request({ url: api.GET_VOUCHER_METADATA, method: 'POST' });
    return data;
  },
};

export const mentalHealthConfigService = createService({
  getList: api.GET_LIST_MH_CONFIGS,
  create: api.CREATE_MH_CONFIG,
  update: api.UPDATE_MH_CONFIG,
  delete: api.DELETE_MH_CONFIG,
  ...(api.CHANGE_MH_CONFIG_STATUS ? {
    changeStatus: async (id, status) => {
      const { data } = await request({ url: api.CHANGE_MH_CONFIG_STATUS, method: 'POST', data: { id, status } });
      return data;
    }
  } : {}),
});

export const serviceBlockService = {
  ...createService({
    getList: api.GET_LIST_SERVICE_BLOCKS,
    create: api.CREATE_SERVICE_BLOCK,
    update: api.UPDATE_SERVICE_BLOCK,
    delete: api.DELETE_SERVICE_BLOCK,
    sort: api.SORT_SERVICE_BLOCKS,
  }),
  changeStatus: async (id, status) => {
    const { data } = await request({ url: api.CHANGE_SERVICE_BLOCK_STATUS, method: 'POST', data: { id, status } });
    return data;
  },
  copy: async (id) => {
    const { data } = await request({ url: api.COPY_SERVICE_BLOCK, method: 'POST', data: { id } });
    return data;
  },
  getMetadata: async () => {
    const { data } = await request({ url: api.GET_SERVICE_BLOCK_METADATA, method: 'POST' });
    return data;
  },
};
